import { useState, memo, useEffect, useMemo } from 'react';
import {
  ComposableMap, Geographies, Geography, ZoomableGroup,
} from 'react-simple-maps';
import { getISO3, isGlobalSouthISO3 } from '../../../utils/countryMapping';
import { parseCountryList } from '../../../utils/dataProcessor/parsers';
import { fetchTopoJSON, getTopoCache, geoToISO3 } from '../shared/constants';
import { useProgressData } from './useProgressData';
import { ChartWrapper } from '../shared/ChartWrapper';

export const WorldMapChart = memo(function WorldMapChart({ countries: countriesProp, countryResultCounts: countsProp }) {
  const { countries: countriesHook, countryResultCounts: countsHook, filteredResults } = useProgressData();
  const countries = countriesProp ?? countriesHook;
  const countryResultCounts = countsProp ?? countsHook;

  const [tooltipContent, setTooltipContent] = useState('');
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  // Prepare chart data for download
  const chartData = useMemo(() => {
    if (!countryResultCounts) return [];
    return Object.entries(countryResultCounts)
      .map(([country, count]) => ({ country, result_count: count }))
      .sort((a, b) => b.result_count - a.result_count);
  }, [countryResultCounts]);
  const [topology, setTopology] = useState(getTopoCache());

  useEffect(() => {
    if (!topology) {
      fetchTopoJSON().then(setTopology);
    }
  }, [topology]);

  const highlightedISO3 = new Set();
  const iso3ToCounts = {};

  if (countries && countries.length > 0) {
    for (const country of countries) {
      const iso3 = getISO3(country);
      if (iso3 && isGlobalSouthISO3(iso3)) {
        highlightedISO3.add(iso3);
        iso3ToCounts[iso3] = (countryResultCounts?.[country] || 1);
      }
    }
  }

  const counts = Object.values(iso3ToCounts);
  const maxCount = counts.length > 0 ? Math.max(...counts) : 1;

  const getColor = (iso3) => {
    if (!highlightedISO3.has(iso3)) return '#E5E7EB';
    const count = iso3ToCounts[iso3] || 1;
    const t = maxCount > 1 ? (count - 1) / (maxCount - 1) : 0;
    const r = Math.round(187 + t * (0 - 187));
    const g = Math.round(247 + t * (104 - 247));
    const b = Math.round(208 + t * (55 - 208));
    return `rgb(${r},${g},${b})`;
  };

  const handleMouseEnter = (geo, evt) => {
    const iso3 = geoToISO3(geo);
    const name = geo.properties.NAME || geo.properties.name || iso3;
    const count = iso3ToCounts[iso3];
    if (highlightedISO3.has(iso3)) {
      setTooltipContent(`${name}: ${count} result${count !== 1 ? 's' : ''}`);
    } else {
      setTooltipContent(name);
    }
    setTooltipPos({ x: evt.clientX, y: evt.clientY });
  };

  const handleMouseLeave = () => { setTooltipContent(''); };
  const handleMouseMove = (evt) => { if (tooltipContent) setTooltipPos({ x: evt.clientX, y: evt.clientY }); };

  if (!topology) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-center aspect-[16/7]">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-3 border-[#006837] border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-gray-500">Loading map data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ChartWrapper title="Results by Location" info="Geographic distribution of all reported results, regardless of partnership status. Darker shading indicates more results in that country. Knowledge products and other results without partners are included." description="Shows all reported results by country, including knowledge products and results without partners. Each country is shaded by the total number of results tagged to it." chartData={chartData} exportFilename="results-by-location" sourceResults={filteredResults}>
      {(() => {
        const withGeo = filteredResults.filter(r => (r.countriesParsed || (r.countries ? parseCountryList(r.countries) : [])).length > 0);
        const w1w2 = withGeo.filter(r => r.fundingType !== 'W3Bilateral').length;
        const w3 = withGeo.filter(r => r.fundingType === 'W3Bilateral').length;
        return (
          <div className="text-xs mb-3 flex items-center gap-1.5 flex-wrap">
            <span style={{ color: '#006837' }}><span className="font-semibold">{w1w2.toLocaleString()}</span> W1/W2 results</span>
            <span className="text-[var(--text-tertiary)]">·</span>
            <span style={{ color: '#F37021' }}><span className="font-semibold">{w3.toLocaleString()}</span> W3/Bilateral results</span>
            <span className="text-[var(--text-tertiary)]">({withGeo.length.toLocaleString()} of {filteredResults.length.toLocaleString()} results with country data)</span>
          </div>
        );
      })()}
      <div className="relative aspect-[16/7]" onMouseMove={handleMouseMove}>
        <ComposableMap projection="geoEquirectangular" projectionConfig={{ rotate: [-10, 0, 0], scale: 130 }} width={800} height={350} style={{ width: '100%', height: '100%' }}>
          <ZoomableGroup>
            <Geographies geography={topology}>
              {({ geographies }) =>
                geographies.map((geo) => {
                  const iso3 = geoToISO3(geo);
                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill={getColor(iso3)}
                      stroke="#fff"
                      strokeWidth={0.5}
                      onMouseEnter={(evt) => handleMouseEnter(geo, evt)}
                      onMouseLeave={handleMouseLeave}
                      style={{
                        default: { outline: 'none' },
                        hover: { fill: highlightedISO3.has(iso3) ? '#004D29' : '#D1D5DB', outline: 'none', cursor: 'pointer' },
                        pressed: { outline: 'none' },
                      }}
                    />
                  );
                })
              }
            </Geographies>
          </ZoomableGroup>
        </ComposableMap>
        {tooltipContent && (
          <div className="fixed z-50 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg shadow-lg pointer-events-none" style={{ left: tooltipPos.x + 12, top: tooltipPos.y - 28 }}>
            {tooltipContent}
          </div>
        )}
      </div>
      {countries && countries.length > 0 && (
        <div className="mt-3 flex items-center justify-center gap-2 text-xs text-gray-500">
          <span>Fewer results</span>
          <div className="w-24 h-3 rounded" style={{ background: 'linear-gradient(to right, #BBF7D0, #006837)' }} />
          <span>More results</span>
        </div>
      )}
    </ChartWrapper>
  );
});
